import axios from 'axios';

/**
 * Service for handling visit statistics
 */
export const statisticsService = {
  // Храним информацию о запросах для предотвращения дублирования
  _requestCache: {},
  _minRequestInterval: 5000, // Минимальный интервал между запросами для одного пути (5 секунд)
  _sessionRequests: {}, // Храним запросы по сессиям
  
  /**
   * Проверяет, не является ли запрос дубликатом
   * @param {string} path - Путь страницы
   * @param {Object} additionalData - Дополнительные данные о посещении
   * @returns {boolean} - true, если запрос можно отправить
   */
  _canSendRequest: function(path, additionalData) {
    const now = Date.now();
    const sessionId = additionalData?.sessionId;
    const visitType = additionalData?.visitType;
    
    // Если это начало сессии или выход со страницы, всегда отправляем
    if (visitType === 'session_start' || visitType === 'page_exit' || visitType === 'session_end') {
      return true;
    }
    
    // Проверяем кэш запросов по пути
    const cacheKey = path;
    const lastRequest = this._requestCache[cacheKey];
    
    if (lastRequest) {
      const timeSinceLastRequest = now - lastRequest.time;
      
      // Если прошло меньше минимального интервала
      if (timeSinceLastRequest < this._minRequestInterval) {
        console.log(`statisticsService: Skipping duplicate request for ${path} (${timeSinceLastRequest}ms since last request)`);
        return false;
      }
    }
    
    // Проверяем, был ли уже запрос для этой сессии и пути
    if (sessionId && visitType === 'page_view') {
      const sessionKey = `${sessionId}_${path}`;
      
      if (this._sessionRequests[sessionKey]) {
        console.log(`statisticsService: Skipping duplicate page_view for session ${sessionId} and path ${path}`);
        return false;
      }
      
      // Запоминаем, что уже отправляли запрос для этой сессии и пути
      this._sessionRequests[sessionKey] = true;
    }
    
    // Обновляем данные последнего запроса
    this._requestCache[cacheKey] = {
      time: now,
      path: path
    };
    
    return true;
  },
  
  /**
   * Получение публичного IP-адреса клиента через внешний сервис
   * @returns {Promise<string|null>} - IP-адрес или null в случае ошибки
   */
  getClientIp: async () => {
    try {
      // Используем бесплатный сервис для определения IP
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error('statisticsService: Error getting client IP:', error);
      return null;
    }
  },
  
  /**
   * Записывает посещение страницы
   * @param {string} path - Путь страницы
   * @param {string} referrer - Источник перехода
   * @param {number|null} timeOnPage - Время на странице в секундах (если null, то не учитывается)
   * @param {Object} additionalData - Дополнительные данные о посещении
   */
  recordVisit: async function(path, referrer, timeOnPage, additionalData = {}) {
    try {
      // Проверяем, не является ли запрос дубликатом
      if (!this._canSendRequest(path, additionalData)) {
        return { success: true, skipped: true };
      }
    } catch (error) {
      console.error('statisticsService: Error checking for duplicate request:', error);
    }
    
    try {
      console.log('statisticsService: Recording visit for path', path, 'with data', { referrer, timeOnPage, additionalData });
      
      // Получаем IP-адрес клиента через внешний сервис
      let clientIp = null;
      try {
        clientIp = await statisticsService.getClientIp();
        console.log('statisticsService: Detected client IP:', clientIp);
      } catch (ipError) {
        console.error('statisticsService: Failed to get client IP:', ipError);
      }
      
      // Получаем информацию о пользователе из localStorage
      let userInfo = null;
      try {
        const userDataStr = localStorage.getItem('user');
        if (userDataStr) {
          const userData = JSON.parse(userDataStr);
          userInfo = {
            userId: userData.id,
            isAdmin: ['admin', 'superadmin'].includes(userData.role),
            role: userData.role,
            name: userData.name
          };
          console.log('statisticsService: User info for statistics:', userInfo);
        }
      } catch (userError) {
        console.error('statisticsService: Error getting user info:', userError);
      }
      
      // Добавляем дополнительные заголовки для лучшей обработки запроса
      const response = await axios.post('/api/statistics/record', {
        path,
        referrer,
        timeOnPage,
        additionalData,
        userInfo,
        clientIp, // Отправляем полученный IP-адрес
        clientTimestamp: Date.now(), // Добавляем время клиента
        userAgent: navigator.userAgent, // Дублируем userAgent в теле запроса
        screenSize: `${window.innerWidth}x${window.innerHeight}`, // Добавляем размер экрана
        isExternalRequest: true // Пометка, что это внешний запрос
      });
      
      // Проверяем ответ сервера
      if (response.data && response.data.isLocalIp) {
        console.log('statisticsService: Visit from local IP, but still processed for diagnostics');
      } else {
        console.log('statisticsService: Visit recorded successfully', response.data);
      }
      
      return response.data;
    } catch (error) {
      console.error('statisticsService: Error recording visit:', error);
      // Добавляем более подробное логирование ошибок
      if (error.response) {
        console.error('statisticsService: Error response data:', error.response.data);
        console.error('statisticsService: Error response status:', error.response.status);
      }
      // Silently fail - we don't want to break the user experience
      return { success: false };
    }
  },

  /**
   * Get statistics data
   * @param {Object} params - Query parameters
   * @returns {Promise} - Promise with the response
   */
  getStatistics: async (params = {}) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/statistics', { 
        headers: {
          Authorization: `Bearer ${token}`
        },
        params 
      });
      return response.data;
    } catch (error) {
      console.error('statisticsService: Error fetching statistics:', error);
      throw error;
    }
  },

  /**
   * Get detailed statistics data
   * @param {Object} params - Query parameters
   * @returns {Promise} - Promise with the response
   */
  getDetailedStatistics: async (params = {}) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/statistics/details', {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params
      });
      return response.data;
    } catch (error) {
      console.error('statisticsService: Error getting detailed statistics:', error);
      throw error;
    }
  },
  
  /**
   * Get dashboard statistics data
   * @param {Object} params - Query parameters (startDate, endDate)
   * @returns {Promise} - Promise with the response
   */
  getDashboardStats: async (params = {}) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/statistics/dashboard', {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params
      });
      return response.data;
    } catch (error) {
      console.error('statisticsService: Error getting dashboard stats:', error);
      throw error;
    }
  }
};

export default statisticsService;
